<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { Plot } from '../models/Plot'
  import { PlotSchema } from '../models/PlotSchema'
  import { PlotFieldType } from '../models/PlotFieldType'
  import ObjectPlotBuilder from './ObjectPlotBuilder.vue'
  import ArrayPlotBuilder from './ArrayPlotBuilder.vue'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { PlottingService } from '../services/PlottingService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import JsonPlotConfiguration from './JsonPlotConfiguration.vue'
  import { PlottingJsonPlotConfigurationService } from '../services/PlottingJsonPlotConfigurationService'
  import ArrayValuesPlotConfiguration from './ArrayValuesPlotConfiguration.vue'
  import SelectOrCreatePlotConfiguration from './SelectOrCreatePlotConfiguration.vue'
  import {
    AppstoreOutlined,
    BorderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import ColumnMultiSelectOrCreatePlotConfiguration from './ColumnMultiSelectOrCreatePlotConfiguration.vue'
  import { PlotField } from '../models/PlotField'
  import PlotHelpPopover from '../help/components/PlotHelpPopover.vue'
  import RegexPlottingConfiguration from './RegexPlottingConfiguration.vue'
  import { PlotSchemaUiFieldGroup } from '../models/PlotSchemaUi'
  import ColorPlotConfiguration from './ColorPlotConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import PlotDataSourceSelectConfiguration from './PlotDataSourceSelectConfiguration.vue'
  import { PlotDataSourceType } from '../models/PlotDataSourceType'
  import { PlotsUiService } from '../services/PlotsUiService'

  interface IPlotConfigurationProps {
    plotSchemas: Array<PlotSchema>
    plot: Plot | null
    uiFieldGroup?: PlotSchemaUiFieldGroup
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      id: string
      value: string
      label: string
      type: PlotDataSourceType
    }>
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IPlotConfigurationEmits {
    (
      e: 'configuration-updated',
      plot: Plot,
      config: Record<string, any>,
      ruleTypeChange?: boolean
    ): void
    (e: 'data-source-selected', value: PlotDataSourceType): void
  }

  const props = defineProps<IPlotConfigurationProps>()
  const emit = defineEmits<IPlotConfigurationEmits>()

  //TODO: add validation should we use forms?
  const formRef = ref()
  const configuration = ref<Record<string, any>>({})
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const plotDescription = ref(props.plot?.configuration?.description ?? '')

  watch(plotDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.plot) return
    configuration.value = PlottingService.initializePlotConfiguration(
      props.plot,
      props.plot.schema.fields
    )
  }

  const handleDataSourceSelected = (value: PlotDataSourceType) => {
    emit('data-source-selected', value)
  }

  const handleConfigurationChange = () => {
    if (props.plot) {
      emit('configuration-updated', props.plot, configuration.value)
    }
  }

  const handleChildPlotTypeChanged = (plotType: string, childPlot: Plot, configuration: any) => {
    if (childPlot) {
      emit('configuration-updated', { ...childPlot, _type: plotType }, configuration, true)
    }
  }

  const handlePlotTypeChanged = (plotType: string, configuration: any) => {
    if (props.plot) {
      emit('configuration-updated', { ...props.plot, _type: plotType }, configuration?.value, true)
    }
  }

  watch(
    () => props.plot,
    (newPlot) => {
      if (newPlot) {
        configuration.value = { ...newPlot.configuration }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          // Ensure JSON Export has initial JSON config state
          handleConfigurationChange()
        }
        // ensure any non-schema properties are initialized
        plotDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props.plot
      ? props.plot.schema.fields.filter(
          (f: any) =>
            f.field_type !== PlotFieldType.Object &&
            f.field_type !== PlotFieldType.Array &&
            f.field_type !== PlotFieldType.Json
        ).length
      : 0
  )

  const configuredFields = computed(() => {
    const calculation = props.plot
    const uiFieldGroup = props.uiFieldGroup

    if (!calculation) {
      return []
    }

    const allFields = calculation.schema.fields

    if (!uiFieldGroup || !Array.isArray(uiFieldGroup.fields) || uiFieldGroup.fields.length === 0) {
      return allFields
    }

    // Ensure order matches uiFieldGroup.fields
    return uiFieldGroup.fields
      .map((fieldName) => allFields.find((field: PlotField) => field.name === fieldName))
      .filter((field): field is PlotField => !!field)
  })

  const levelBasedBackgroundColor = computed(() => {
    // backgroundColorClamp configurationLevel to min 1
    const level = Math.max(props.configurationLevel, 1)
    // Opacity increases by 1% per level, starting from 0% at level 1
    const opacity = Math.min((level - 1) * 0.005, 1)
    return `rgba(0, 0, 0, ${opacity.toFixed(2)})`
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.plot?.schema?.fields?.findIndex(
        (x: PlotField) =>
          x.field_type === PlotFieldType.Array ||
          x.field_type === PlotFieldType.Object ||
          x.field_type === PlotFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: PlotField): boolean => {
    return field.field_type !== PlotFieldType.Object && field.field_type !== PlotFieldType.Json
  }

  const { controlSize } = useWindowSize()
  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.plot?.display_field as string | Array<string> | null | undefined
    if (mappedField == null) return undefined

    const fields = props.plot?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try {
            inputEl.setSelectionRange(cursor, cursor)
          } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div
    v-if="plot"
    :style="{ backgroundColor: levelBasedBackgroundColor }"
    :class="[
      'border',
      'plot-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-plot-configuration': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <!-- Only show radio group if configurationLevel is 1 or less -->
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mb-1 text-lg font-semibold">
              Configure: {{ plot.display_name }}
              <span class="ml-1" v-if="displayField != null">{{ `(${displayField})` }}</span>
            </h3>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          plot._type !== PlottingService.CUSTOM_PLOT_TYPE && props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Description</label>
        <Input
          v-model:value="plotDescription"
          @change="handleConfigurationChange"
          placeholder="Plot description"
          :size="controlSize"
        />
      </div>

      <!-- Configuration Form -->
      <div :class="['flex-1', 'overflow-y-auto', { 'collapsed-plot-configuration': isCollapsed }]">
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'plot-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredFields"
              :key="configuredField.name"
              class="plot-form-field-grid"
              :class="{
                'array-plot-builder-grid-span': configuredField.field_type === PlotFieldType.Array,
                'object-plot-builder-grid-span':
                  configuredField.field_type === PlotFieldType.Object ||
                  configuredField.field_type === PlotFieldType.StringTemplate,
                'json-plot-configuration-grid-span':
                  configuredField.field_type === PlotFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field_type !== PlotFieldType.Object &&
                  configuredField.field_type !== PlotFieldType.Array &&
                  configuredField.field_type !== PlotFieldType.Json,
                '!hidden': !PlotsUiService.isFieldVisibleFromRoot(
                  configuredField,
                  props.plot,
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                )
              }"
            >
              <div class="flex">
                <div v-if="shouldShowFieldInformation(configuredField)" class="min-w-0 flex-1">
                  <label class="table-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.display_name }}
                    <span v-if="configuredField.required" class="text-red-500">*</span>
                  </label>
                  <p v-if="configuredField.description" class="table-field-label mb-2 text-xs">
                    {{ configuredField.description }}
                  </p>
                </div>

                <div
                  v-if="
                    configuredField.help_content != null && configuredField.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <PlotHelpPopover
                    :content-type="configuredField.help_content"
                    :title="`${configuredField.display_name} Help`"
                  />
                </div>
              </div>
              <!-- Text Input -->
              <Input
                v-if="configuredField.field_type === PlotFieldType.Text"
                :value="configuration[configuredField.name]"
                @update:value="(val: string) => onInputChange(val, configuredField.name)"
                @blur="() => onInputBlur(configuredField.name)"
                :data-field="`${props.configurationLevel}:${configuredField.name}`"
                :placeholder="configuredField.placeholder || ''"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field_type === PlotFieldType.Number"
                v-model:value="configuration[configuredField.name]"
                :placeholder="configuredField.placeholder || ''"
                :size="controlSize"
                @change="handleConfigurationChange"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
              />

              <RegexPlottingConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.RegularExpression"
                v-model="configuration[configuredField.name]"
                :required="configuredField.required"
                :regex="configuredField.field_type_args?.regex_pattern"
                :data-type="configuredField.field_type_args?.regex_data_type"
                :placeholder="configuredField.placeholder || ''"
                :error-message="configuredField.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field_type === PlotFieldType.Select"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field_type === PlotFieldType.SelectJson"
                :value="JSON.stringify(configuration[configuredField.name])"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      configuration[configuredField.name] = parsed
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreatePlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.SelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()}`"
                :options="configuredField.options"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field_type === PlotFieldType.MultiSelect ||
                  configuredField.field_type === PlotFieldType.ColumnMultiSelect ||
                  configuredField.field_type === PlotFieldType.ParamMultiSelect ||
                  configuredField.field_type === PlotFieldType.ParamOrColumnMultiSelect
                "
                v-model:value="configuration[configuredField.name]"
                mode="multiple"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field_type === PlotFieldType.MultiSelect"
                  v-for="option in configuredField.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === PlotFieldType.ColumnMultiSelect"
                  v-for="option in props.availableColumns"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === PlotFieldType.ParamMultiSelect"
                  v-for="option in props.availableParams"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>

                <SelectOption
                  v-if="configuredField.field_type === PlotFieldType.ParamOrColumnMultiSelect"
                  v-for="option in [...props.availableColumns, ...props.availableParams]"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field_type === PlotFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="configuration[configuredField.name]"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.display_name }}
              </Checkbox>

              <!-- Column Select -->
              <Select
                v-else-if="configuredField.field_type === PlotFieldType.ColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableColumns"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Param Select -->
              <Select
                v-else-if="configuredField.field_type === PlotFieldType.ParamSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableParams"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Param or Column Select -->
              <Select
                v-else-if="configuredField.field_type === PlotFieldType.ParamOrColumnSelect"
                v-model:value="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in [...props.availableColumns, ...props.availableParams]"
                  :key="column.value"
                  :value="column.value"
                >
                  {{ column.label }}
                </SelectOption>
              </Select>

              <!-- Column Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === PlotFieldType.ColumnSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableColumns"
                :placeholder="
                  configuredField.placeholder || `Select existing column or enter new column name`
                "
                :size="controlSize"
                class="w-full"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Param Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === PlotFieldType.ParamSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="props.availableParams"
                :placeholder="
                  configuredField.placeholder || `Select existing param or enter new param`
                "
                :size="controlSize"
                class="w-full"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Param or Column Select or Create -->
              <AutoComplete
                v-else-if="configuredField.field_type === PlotFieldType.ParamOrColumnSelectOrCreate"
                v-model:value="configuration[configuredField.name]"
                :options="[...props.availableColumns, ...props.availableParams]"
                :placeholder="
                  configuredField.placeholder || `Select existing param or column enter new value`
                "
                :size="controlSize"
                class="w-full"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Multi Column Select Or Create -->
              <ColumnMultiSelectOrCreatePlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.ColumnMultiSelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableColumns"
                :disabled="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Param Column Multi Select Or Create -->
              <ColumnMultiSelectOrCreatePlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.ParamMultiSelectOrCreate"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="props.availableParams"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Param Or Column Multi Select Or Create -->
              <ColumnMultiSelectOrCreatePlotConfiguration
                v-else-if="
                  configuredField.field_type === PlotFieldType.ParamOrColumnMultiSelectOrCreate
                "
                v-model="configuration[configuredField.name]"
                :placeholder="`Select or enter new ${configuredField.display_name.toLowerCase()} values`"
                :available-columns="[...props.availableColumns, ...props.availableParams]"
                :readonly="configuredField.readonly"
                :disabled="configuredField.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Values -->
              <ArrayValuesPlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.ArrayValues"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Picker -->
              <ColorPlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.ColorPicker"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select DataSource -->
              <PlotDataSourceSelectConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.SelectDataSource"
                v-model="configuration[configuredField.name]"
                :placeholder="`Select ${configuredField.display_name.toLowerCase()} value`"
                :available-data-sources="props.availableDataSources"
                :data-source-types="configuredField.field_type_args.datasource_types"
                @update:modelValue="handleConfigurationChange"
                @data-source-selected="handleDataSourceSelected"
              />

              <!-- Array Type -->
              <ArrayPlotBuilder
                v-else-if="configuredField.field_type === PlotFieldType.Array"
                :configuration-level="props.configurationLevel"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                :available-data-sources="props.availableDataSources"
                v-model="configuration[configuredField.name]"
                :plot-field="configuredField"
                :key="plot.id"
                @update:modelValue="handleConfigurationChange"
                @child-plot-type-changed="handleChildPlotTypeChanged"
                @data-source-selected="handleDataSourceSelected"
                :plot-schemas="plotSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Object Type -->
              <ObjectPlotBuilder
                v-else-if="configuredField.field_type === PlotFieldType.Object"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :available-params="props.availableParams"
                :available-data-sources="props.availableDataSources"
                v-model="configuration[configuredField.name]"
                :plot-field="configuredField"
                @update:modelValue="handleConfigurationChange"
                @data-source-selected="handleDataSourceSelected"
                :plot-schemas="plotSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :root-plot="props.plot"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, configuredField.name]
                    : [configuredField.name]
                "
              />

              <!-- String Template -->
              <StringTemplateBuilder
                v-else-if="configuredField.field_type === PlotFieldType.StringTemplate"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :delimiter_start="configuredField.field_type_args?.delimiter_start"
                :delimiter_end="configuredField.field_type_args?.delimiter_end"
                v-model="configuration[configuredField.name]"
                :place-holder="configuredField.placeholder"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Json Type (Custom Rule) -->
              <JsonPlotConfiguration
                v-else-if="configuredField.field_type === PlotFieldType.Json"
                :plot-schemas="props.plotSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration"
                :editor-schema="PlottingJsonPlotConfigurationService.getJsonEditorSchemaForPlot()"
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @plot-type-changed="handlePlotTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonPlotConfiguration
                v-else
                :plot-schemas="props.plotSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration[configuredField.name]"
                :editor-schema="
                  PlottingJsonPlotConfigurationService.getJsonEditorSchemaForField(configuredField)
                "
                :place-holder="configuredField.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Rule Selected</p>
      <p class="text-sm">Select a rule from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .plot-configuration {
    padding: 10px;
  }

  .collapsed-plot-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .plot-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .plot-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .plot-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  /* Ensure ArrayRuleBuilder, ObjectRuleBuilder, JsonRuleConfiguration, and single simple field always span all columns */
  .array-plot-builder-grid-span,
  .object-plot-builder-grid-span,
  .json-plot-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .plot-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .plot-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }
  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }
  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
